import React, { useEffect, useState, useRef, useCallback } from "react";
import { useIntl } from "react-intl";
import videojs from "video.js";
import "video.js/dist/video-js.css";
import Transcript from "./transcript/videojs-transcript";

import "./index.scss";

/**
 * Register a Video.js custom Button (once) for toggling transcript visibility.
 * Uses aria-pressed + aria-controls for accessibility.
 */
function ensureTranscriptToggleRegistered(vjs) {
  // Avoid double registration in hot reload / multiple mounts
  try {
    if (vjs.getComponent("TranscriptToggle")) return;
  } catch (e) {
    // ignore
  }

  const Button = vjs.getComponent("Button");

  class TranscriptToggle extends Button {
    constructor(player, options) {
      super(player, options);

      this.options_ = options || {};
      this.addClass("transcript-btn");

      // Accessible name (SR)
      this.controlText(this.options_.getLabel?.() || "Transcript");

      // Button semantics
      this.el().setAttribute("type", "button");

      // aria-controls ties the button to the transcript panel container
      if (this.options_.controlsId) {
        this.el().setAttribute("aria-controls", this.options_.controlsId);
      }

      // Visible label (for sighted users)
      this.labelEl_ = document.createElement("span");
      this.labelEl_.className = "transcript-btn-label";
      this.labelEl_.setAttribute("aria-hidden", "true"); // don't double-announce
      this.labelEl_.textContent = this.options_.getLabel?.() || "Transcript";
      this.el().appendChild(this.labelEl_);

      // Set initial pressed state
      const isOn = !!this.options_.isOn?.();
      this.el().setAttribute("aria-pressed", String(isOn));

      // If transcript not available, hide and remove from tab order
      const hasTranscript = !!this.options_.hasTranscript?.();
      if (!hasTranscript) {
        this.el().style.display = "none";
        this.el().setAttribute("aria-hidden", "true");
        this.el().setAttribute("tabindex", "-1");
      }
    }

    handleClick() {
      const isOn = !!this.options_.isOn?.();
      const next = !isOn;

      // Update React state via callback
      this.options_.onToggle?.(next);

      // Update aria-pressed for SR users
      this.el().setAttribute("aria-pressed", String(next));
    }

    update() {
      // Update SR label + visible label + pressed state
      const label = this.options_.getLabel?.() || "Transcript";
      this.controlText(label);

      if (this.labelEl_) {
        this.labelEl_.textContent = label;
      }

      const isOn = !!this.options_.isOn?.();
      this.el().setAttribute("aria-pressed", String(isOn));

      const hasTranscript = !!this.options_.hasTranscript?.();
      if (!hasTranscript) {
        this.el().style.display = "none";
        this.el().setAttribute("aria-hidden", "true");
        this.el().setAttribute("tabindex", "-1");
      } else {
        this.el().style.display = "";
        this.el().removeAttribute("aria-hidden");
        this.el().removeAttribute("tabindex");
      }
    }
  }

  vjs.registerComponent("TranscriptToggle", TranscriptToggle);
}

export function VideoPlayer(props) {
  const intl = useIntl();

  const [player, setPlayer] = useState(null);
  const [viewTranscript, setViewTranscript] = useState(false);

  const videoNode = useRef(null);
  const transcriptArea = useRef(null);

  // Keep latest transcript visibility in a ref so Video.js button can read it reliably
  const viewTranscriptRef = useRef(false);
  useEffect(() => {
    viewTranscriptRef.current = viewTranscript;
  }, [viewTranscript]);

  // Keep latest transcript availability in a ref
  const hasTranscriptRef = useRef(false);
  useEffect(() => {
    hasTranscriptRef.current = !!props.data?.srt;
  }, [props.data?.srt]);

  const transcriptContainerId = "videojs-transcript-panel";

  const cleanupTracks = useCallback(() => {
    if (!videoNode.current || !player) return;

    // Remove native <track> elements
    videoNode.current.querySelectorAll("track").forEach((t) => t.remove());

    // Remove Video.js remote text tracks
    Array.from(player.remoteTextTracks()).forEach((t) => {
      try {
        player.removeRemoteTextTrack(t);
      } catch (e) {
        // ignore
      }
    });
  }, [player]);

  const loadSafariCaption = useCallback(
    (vttUrl) => {
      if (!videoNode.current) return;

      const trackEl = document.createElement("track");
      trackEl.kind = "captions";
      trackEl.srclang = intl.locale;
      trackEl.label = intl.locale === "en" ? "English" : intl.locale;
      trackEl.default = true;
      trackEl.src = vttUrl;
      videoNode.current.appendChild(trackEl);

      // Force Safari to refresh text track rendering
      const [txt] = videoNode.current.textTracks;
      if (txt) {
        txt.mode = "hidden";
        txt.mode = "showing";
      }
    },
    [intl.locale]
  );

  const loadRemoteCaption = useCallback(
    (vttUrl) => {
      if (!player) return;

      const remote = player.addRemoteTextTrack(
        {
          kind: "captions",
          srclang: intl.locale,
          label: intl.locale === "en" ? "English" : intl.locale,
          src: vttUrl,
          default: true,
          manualCleanup: true,
        },
        false
      );

      const txt = remote.track;
      if (txt) {
        txt.mode = "hidden";
        txt.mode = "showing";
      }
    },
    [player, intl.locale]
  );

  const convertSrtCue = useCallback((caption) => {
    const lines = caption.split(/\n/);

    // Merge extra lines into cue text
    while (lines.length > 3) {
      lines[2] += "\n" + lines.splice(3, 1);
    }

    let idx = 0;
    let cueText = "";

    // Handle optional cue identifier line
    if (!/\d+:\d+:\d+/.test(lines[0]) && /\d+:\d+:\d+/.test(lines[1])) {
      cueText += lines[0] + "\n";
      idx = 1;
    }

    const timeMatch = lines[idx]?.match(
      /(\d+):(\d+):(\d+)(?:,(\d+))?\s*-->\s*(\d+):(\d+):(\d+)(?:,(\d+))?/
    );
    if (!timeMatch) return "";

    const startMs = timeMatch[4] ?? "000";
    const endMs = timeMatch[8] ?? "000";

    cueText +=
      `${timeMatch[1]}:${timeMatch[2]}:${timeMatch[3]}.${startMs} --> ` +
      `${timeMatch[5]}:${timeMatch[6]}:${timeMatch[7]}.${endMs}\n`;

    cueText += (lines[idx + 1] ?? "") + "\n\n";
    return cueText;
  }, []);

  const srt2webvtt = useCallback(
    (data) => {
      const srt = data.replace(/\r+/g, "").trim();
      const cuelist = srt.split("\n\n");

      let result = "WEBVTT\n\n";
      cuelist.forEach((cue) => {
        result += convertSrtCue(cue);
      });

      return result;
    },
    [convertSrtCue]
  );

  const initializeTranscript = useCallback(() => {
    if (!player || !transcriptArea.current) return;

    // Let Video.js flush any track changes, then mount transcript UI
    setTimeout(() => {
      const transcript = player.transcript({
        showTitle: false,
        showTrackSelector: false,
      });
      const node = transcriptArea.current;
      node.innerHTML = "";
      node.appendChild(transcript.el());

      // Add keyboard support for transcript navigation
      // Wait for the transcript to be fully rendered
      setTimeout(() => {
        const transcriptLines = node.querySelectorAll(".transcript-line");
        transcriptLines.forEach((line) => {
          // Add button role for screen readers
          line.setAttribute("role", "button");

          // Get timestamp for aria-label
          const timestamp =
            line.querySelector(".transcript-timestamp")?.textContent || "";
          const text =
            line.querySelector(".transcript-text")?.textContent || "";

          if (timestamp && text) {
            line.setAttribute("aria-label", `Seek to ${timestamp}: ${text}`);
          }

          // Add keyboard event handler
          line.addEventListener("keydown", (e) => {
            // Activate on Enter or Space
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();

              // Get the timestamp from data-begin attribute
              const timeToSeek = line.getAttribute("data-begin");
              if (timeToSeek !== null && timeToSeek !== undefined) {
                player.currentTime(parseFloat(timeToSeek));
              }
            }
          });
        });
      }, 100);
    }, 0);
  }, [player]);

  const setTranscriptData = useCallback(
    (data) => {
      const vttBlob = new Blob([srt2webvtt(data)], {
        type: "text/vtt;charset=utf-8",
      });
      const vttUrl = URL.createObjectURL(vttBlob);

      cleanupTracks();

      if (videojs.browser.IS_SAFARI) {
        loadSafariCaption(vttUrl);
      } else {
        loadRemoteCaption(vttUrl);
      }

      initializeTranscript();
    },
    [
      cleanupTracks,
      initializeTranscript,
      loadRemoteCaption,
      loadSafariCaption,
      srt2webvtt,
    ]
  );

  const getTranscript = useCallback(async () => {
    if (!props.data?.srt) return;

    const baseUrl =
      window.CONTENT_BASE_URL ||
      window.location.origin + window.location.pathname;

    try {
      let url = props.preview
        ? props.data.srtURL
        : `${baseUrl}locales/${intl.locale}/srt/${props.data.srt}`;

      let response = await fetch(url);
      let data = await response.text();

      // Fallback to English if localized file is missing/returns HTML error
      if (data.includes("!DOCTYPE") || data.includes("<Error>")) {
        url = `${baseUrl}locales/en/srt/${props.data.srt}`;
        response = await fetch(url);
        data = await response.text();
      }

      setTranscriptData(data);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Error fetching transcript:", error);
    }
  }, [
    props.data?.srt,
    props.data?.srtURL,
    props.preview,
    intl.locale,
    setTranscriptData,
  ]);

  // Initialize Video.js player (runs once)
  useEffect(() => {
    if (!videoNode.current) return;

    ensureTranscriptToggleRegistered(videojs);
    Transcript(window, videojs);

    const instance = videojs(videoNode.current, {
      sources: [{ src: props.data.videoURL, type: "application/x-mpegURL" }],

      // DOM/tab order is controlled here:
      // Play/Pause -> Volume -> Captions -> Transcript -> Playback -> Fullscreen -> Progress
      controlBar: {
        children: [
          "playToggle",
          "volumePanel",
          "captionsButton",
          "TranscriptToggle",
          "playbackRateMenuButton",
          "fullscreenToggle",
          "progressControl",
          "currentTimeDisplay",
          "timeDivider",
          "durationDisplay",
        ],
        playToggle: true,
        captionsButton: true,
        chaptersButton: false,
        subtitlesButton: false,
        currentTime: true,
        currentTimeDisplay: true,
        liveCurrentTime: true,
        durationDisplay: true,
        progressControl: { seekBar: true },
        fullscreenToggle: true,
        volumePanel: { inline: true },
        playbackRateMenuButton: true,
        pictureInPictureToggle: false,
      },

      playbackRates: [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2],
      castButton: false,
      textTrackSettings: true,
    });

    setPlayer(instance);

    instance.ready(() => {
      // Configure transcript button options (link it to React state)
      const transcriptBtn = instance.controlBar?.getChild("TranscriptToggle");
      if (transcriptBtn) {
        transcriptBtn.options_.getLabel = () =>
          intl.formatMessage({
            id: "Transcript",
            defaultMessage: "Transcript",
          });

        transcriptBtn.options_.controlsId = transcriptContainerId;
        transcriptBtn.options_.onToggle = (next) => setViewTranscript(next);
        transcriptBtn.options_.isOn = () => viewTranscriptRef.current;
        transcriptBtn.options_.hasTranscript = () => hasTranscriptRef.current;

        // Sync initial state/label/visibility
        transcriptBtn.update?.();
      }

      // Load transcript if available
      if (props.data?.srt) {
        getTranscript();
      }
    });

    return () => {
      try {
        instance.dispose();
      } catch (e) {
        // ignore
      }
    };
  }, []);

  // When video URL changes, update the source + transcript
  useEffect(() => {
    if (!player || !videoNode.current) return;

    if (!player.paused()) {
      videoNode.current.pause();
    }

    cleanupTracks();
    player.src([{ src: props.data.videoURL, type: "application/x-mpegURL" }]);
    videoNode.current.load();

    if (props.data?.srt) {
      getTranscript();
    }
  }, [
    props.data.videoURL,
    player,
    cleanupTracks,
    getTranscript,
    props.data?.srt,
  ]);

  // When locale changes or transcript file changes, refetch transcript + update transcript button label
  useEffect(() => {
    if (!player) return;

    const transcriptBtn = player.controlBar?.getChild("TranscriptToggle");
    if (transcriptBtn) {
      transcriptBtn.update?.();
    }

    if (props.data?.srt) {
      getTranscript();
    }
  }, [intl.locale, props.data?.srt, player, getTranscript]);

  return (
    <div className="basicVideo" id="basicVideo">
      <div className="video-container">
        {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
        <video
          ref={videoNode}
          crossOrigin="anonymous"
          className="video-js custom-videojs-components vjs-big-play-centered vjs-fluid"
          id="my-player"
          controls
          preload="true"
        >
          <p className="vjs-no-js">
            To view this video please enable JavaScript, and consider upgrading
            to a web browser that{" "}
            <a
              href="https://videojs.com/html5-video-support/"
              target="_blank"
              rel="noopener noreferrer"
            >
              supports HTML5 video
              <span className="sr-only"> (opens in a new tab)</span>
            </a>
          </p>
        </video>

        <div
          id={transcriptContainerId}
          className="custom-video-js-transcript"
          ref={transcriptArea}
          role="region"
          aria-label={intl.formatMessage({
            id: "videoTranscript",
            defaultMessage: "Video Transcript",
          })}
          aria-hidden={!viewTranscript}
          style={{ display: !viewTranscript ? "none" : undefined }}
        />
      </div>
    </div>
  );
}

VideoPlayer.propTypes = {};
VideoPlayer.defaultProps = {};
